import { yupResolver } from '@hookform/resolvers/yup';
import { PhoneNumberUtil } from 'google-libphonenumber';
import * as yup from 'yup';
import YupPassword from 'yup-password';
import { IntegrationType } from './consts';

YupPassword(yup);

const phoneUtil = PhoneNumberUtil.getInstance();

export const isPhoneValid = (phone: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch (error) {
    return false;
  }
};

yup.addMethod(yup.string, 'phoneValidation', function () {
  return this.test(
    `test-phone-number`,
    'Phone number is not valid',
    (str, context) => {
      if (context?.schema?.spec?.optional && (!str || str === '+1')) {
        return true;
      }

      if (!str && !context?.schema?.spec?.optional) return false;

      const pn = isPhoneValid(str as string);

      return pn;
    }
  );
});

const EMAIL_REGEX =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const loginValidation = yupResolver(
  yup
    .object({
      email: yup
        .string()
        .trim()
        .matches(EMAIL_REGEX, 'Please enter a valid email address')
        .max(255)
        .required('Email is required'),
      password: yup.string().trim().required().label('Password'),
    })
    .required()
);

export const passwordValidation = yup
  .string()
  .trim()
  .password()
  .minLowercase(1, 'A password must contain at least 1 lowercase')
  .minUppercase(1, 'A password must contain at least 1 Uppercase')
  .minNumbers(1, 'A password must contain at least 1 number')
  .minSymbols(1, 'A password must contain at least 1 symbol')
  .min(8, 'A password must be at leat 8 characters long')
  .required()
  .label('Password');

export const getPasswordValidationSchema = (required: boolean = true) => {
  const schema = yup
    .string()
    .trim()
    .password()
    .minLowercase(1, 'A password must contain at least 1 lowercase')
    .minUppercase(1, 'A password must contain at least 1 Uppercase')
    .minNumbers(1, 'A password must contain at least 1 number')
    .minSymbols(1, 'A password must contain at least 1 symbol')
    .min(8, 'A password must be at leat 8 characters long')
    .label('Password');

  return required ? schema.required() : schema.optional();
};

export const registrationValidation = yupResolver(
  yup.object({
    fullName: yup
      .string()
      .trim()
      .min(1)
      .label('Full Name')
      .max(255)
      .required('Full name is required'),
    email: yup
      .string()
      .matches(EMAIL_REGEX, 'Invalid email format')
      .trim()
      .min(1)
      .max(255)
      .required('Email is required'),
    password: passwordValidation,
    confirmPassword: yup
      .string()
      .trim()
      .password()
      .minLowercase(1, 'A password must contain at least 1 lowercase')
      .minUppercase(1, 'A password must contain at least 1 Uppercase')
      .minNumbers(1, 'A password must contain at least 1 number')
      .minSymbols(1, 'A password must contain at least 1 symbol')
      .min(8, 'A password must be at leat 8 characters long')
      .required()
      .oneOf([yup.ref('password')], 'Your passwords do not match.')
      .label('Confirm Password'),
    phoneNumber: yup
      .string()
      .trim()
      .phoneValidation()
      .optional()
      .label('Phone Number'),
    businessName: yup
      .string()
      .trim()
      .min(1)
      .label('Business Name')
      .max(255)
      .required('Business name is required'),
    businessEmail: yup
      .string()
      .trim()
      .matches(EMAIL_REGEX, 'Invalid email format')
      .optional()
      .label('Business Email'),
    businessCategory: yup.string().required('Business category is required'),
    businessAddress: yup
      .string()
      .trim()
      .label('Business Address')
      .min(1)
      .max(255)
      .required('Business address is required'),
    teamSize: yup.string().trim().label('Team Size').optional(),
    terms: yup
      .boolean()
      .oneOf([true], 'You must accept the Terms of Service and Privacy Policy')
      .required('You must accept the Terms of Service and Privacy Policy'),
  })
);

export const newPasswordValidation = yupResolver(
  yup.object({
    password: yup
      .string()
      .trim()
      .password()
      .minLowercase(1, 'A password must contain at least 1 lowercase')
      .minUppercase(1, 'A password must contain at least 1 Uppercase')
      .minNumbers(1, 'A password must contain at least 1 number')
      .minSymbols(1, 'A password must contain at least 1 symbol')
      .min(8, 'A password must be at leat 8 characters long')
      .required()
      .label('Password'),
    confirmPassword: yup
      .string()
      .trim()
      .password()
      .minLowercase(1, 'A password must contain at least 1 lowercase')
      .minUppercase(1, 'A password must contain at least 1 Uppercase')
      .minNumbers(1, 'A password must contain at least 1 number')
      .minSymbols(1, 'A password must contain at least 1 symbol')
      .min(8, 'A password must be at leat 8 characters long')
      .required()
      .oneOf([yup.ref('password')], 'Your passwords do not match.')
      .label('Password'),
  })
);

export const forgotPasswordValidation = yupResolver(
  yup
    .object({
      email: yup
        .string()
        .trim()
        .matches(EMAIL_REGEX, 'Enter a valid email address')
        .min(1)
        .max(255)
        .required('Email is required'),
    })
    .required()
);

export const createCustomerValidation = yupResolver(
  yup.object({
    firstName: yup
      .string()
      .min(1)
      .label('First name')
      .trim()
      .required('First name is required'),
    lastName: yup
      .string()
      .min(1)
      .label('Last name')
      .trim()
      .required('Last name is required'),
    email: yup
      .string()
      .trim()
      .matches(EMAIL_REGEX, 'Invalid email format')
      .required('Email is required'),
    phoneNumber: yup
      .string()
      .trim()
      .phoneValidation()
      .optional()
      .label('Phone Number'),
    notes: yup.string().trim().optional(),
    tags: yup.array().of(yup.string().trim()).optional(),
    lists: yup.array().of(yup.string().trim()).optional(),
  })
);

export const createListValidation = yupResolver(
  yup.object({
    name: yup.string().min(1).label('Name').trim().required('Name is required'),
    customers: yup.array().of(yup.string().trim()).optional(),
    notes: yup.string().trim().optional(),
  })
);

export const createTagValidation = yupResolver(
  yup.object({
    name: yup.string().min(1).label('Name').trim().required('Name is required'),
    customers: yup.array().of(yup.string().trim()).optional(),
  })
);

export const newBusinessValidation = yupResolver(
  yup.object({
    name: yup.string().min(1).label('Name').trim().required('Name is required'),
    email: yup
      .string()
      .trim()
      .matches(EMAIL_REGEX, 'Invalid email format')
      .required('Business email is required'),
    phoneNumber: yup
      .string()
      .trim()
      .phoneValidation()
      .optional()
      .label('Phone Number'),
    category: yup
      .string()
      .min(1)
      .label('Category')
      .trim()
      .required('Category is required'),
    address: yup
      .string()
      .min(1)
      .label('Address')
      .trim()
      .required('Address is required'),
  })
);

export const updatePasswordEmailValidation = yupResolver(
  yup.object({
    email: yup
      .string()
      .trim()
      .label('Email')
      .matches(EMAIL_REGEX, 'Invalid email format')
      .required('Email is required'),
  })
);

export const updateProfileEmailValidation = yupResolver(
  yup.object({
    email: yup
      .string()
      .trim()
      .label('Email')
      .matches(EMAIL_REGEX, 'Invalid email format')
      .required('Email is required'),
  })
);

export const updateProfilePhoneValidation = yupResolver(
  yup.object({
    phoneNumber: yup
      .string()
      .trim()
      .phoneValidation()
      .required()
      .label('Phone Number'),
  })
);

export const updatePhoneValidation = yupResolver(
  yup.object({
    code: yup
      .string()
      .trim()
      .min(1)
      .label('OTP Code')
      .required('OTP Code is required'),
  })
);

export const updateProfileValidation = yupResolver(
  yup.object({
    firstName: yup
      .string()
      .min(1)
      .trim()
      .label('First Name')
      .required('First name is required'),
    lastName: yup
      .string()
      .min(1)
      .trim()
      .label('Last Name')
      .required('Last name is required'),
  })
);

export const createIntegrationValidation = yupResolver(
  yup.object({
    type: yup
      .string()
      .oneOf(Object.values(IntegrationType))
      .trim()
      .required('Invalid integration type'),
    connectionId: yup.string().trim().optional(),
  })
);

export const updateIntegrationValidation = yupResolver(
  yup.object({
    type: yup
      .string()
      .oneOf(Object.values(IntegrationType))
      .trim()
      .required('Invalid integration type'),
    connectionId: yup.string().trim().optional(),
  })
);

export const updateCustomerValidation = yupResolver(
  yup.object({
    firstName: yup.string().min(1).trim().required('First name is required'),
    lastName: yup.string().min(1).trim().required('Last name is required'),
    email: yup
      .string()
      .trim()
      .matches(EMAIL_REGEX, 'Invalid email format')
      .required('Email is required'),
    phoneNumber: yup
      .string()
      .trim()
      .phoneValidation()
      .optional()
      .label('Phone Number'),
    notes: yup.string().trim().optional(),
    tags: yup.array().of(yup.string().trim()).optional(),
    lists: yup.array().of(yup.string().trim()).optional(),
  })
);

export const adminUserUpdateValidation = yupResolver(
  yup.object({
    firstName: yup
      .string()
      .min(1)
      .trim()
      .label('First name')
      .required('First name is required'),
    lastName: yup
      .string()
      .min(1)
      .trim()
      .label('Last name')
      .required('Last name is required'),
    email: yup
      .string()
      .trim()
      .matches(EMAIL_REGEX, 'Invalid email format')
      .required('Email is required'),
    phoneNumber: yup
      .string()
      .trim()
      .phoneValidation()
      .optional()
      .label('Phone Number'),
    password: yup.string().when({
      is: (value: string) => value && value.length > 0,
      then: () => getPasswordValidationSchema(false),
      otherwise: (schema) => schema.optional(),
    }),
    confirmPassword: yup.string().when('password', {
      is: (password: string) => password && password.length > 0,
      then: (schema) =>
        schema
          .required('Confirm password is required')
          .oneOf([yup.ref('password')], 'Passwords must match'),
      otherwise: (schema) => schema.optional(),
    }),
    role: yup.string().oneOf(['ADMIN', 'USER']),
  })
);
